/**
 * OgreClan AI: ask competitive questions, answered with OgreClan's own data (Showdown's Pokémon data, formats, and your
 * leagues). Conversations are saved to your account (apps/api/src/ai/chats.ts): the list on the side, each one carried
 * on where you left it, renamed, deleted, or all cleared. The server does all the work: this page only sends a question
 * and shows answers, as plain text.
 *
 * The same page at chat.ogreclan.com (its own address, where it's the front page) and at draft.ogreclan.com/chat.
 * /chat/<id> is one conversation; ?league=discord:2, ?format=<format> or ?page=/discord/2 open a new one about that.
 */
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { api, ApiError, signInUrl } from '../api';
import { useMe } from '../me';
import { Link, navigate } from '../router';
import { ago, Loading } from '../ui';

interface LeagueOption { ref: string, name: string, kind: 'discord' | 'showdown' }
interface Status {
	enabled: boolean;
	allowed: boolean;
	/** Who may use it right now (an administrator chooses on the admin page) */
	access?: 'admins' | 'staff' | 'linked' | 'signed-in';
	provider: 'openai' | 'gemini';
	model: string;
	/** Gemini through Google Cloud (Vertex AI), whose terms say Google doesn't train on what's sent */
	vertex: boolean;
	/** A free model, whose provider may use what's sent to it */
	freeTier: boolean;
	leagues: LeagueOption[];
	today: { questions: number, limit: number } | null;
}
interface ChatSummary {
	id: string;
	title: string;
	/** web, discord_dm (a Discord DM conversation), discord_thread */
	source: string;
	league: string | null;
	format: string | null;
	createdAt: string;
	updatedAt: string;
}
interface Message {
	id: number | string;
	role: 'user' | 'assistant';
	content: string;
	meta?: { league?: string | null, format?: string | null, truncated?: boolean };
}
interface Reply {
	requestId: string;
	kind: 'answer' | 'clarify';
	question?: string;
	options?: LeagueOption[];
	chat?: ChatSummary;
	messages?: Message[];
}

/** The page the chat was opened from (a league's), which the server checks before using */
function fromPage() {
	const page = new URLSearchParams(location.search).get('page') || '';
	return /^\/(discord\/\d+|league\/[a-z0-9]+)$/.test(page) ? page : undefined;
}

const SOURCES: Record<string, string> = { discord_dm: 'Discord DM', discord_thread: 'Discord channel' };

function Notice({ status }: { status: Status }) {
	if (!status.enabled) return <p class="notice">OgreClan AI isn't set up yet. Check back soon.</p>;
	return <p class="notice">{
		status.access === 'admins' ? `OgreClan AI is only open to the site's administrators right now.` :
		status.access === 'staff' ? `OgreClan AI is only open to OgreClan's staff right now.` :
		status.access === 'linked' ? <>Link your Showdown name on <a href="/account">your account page</a> to use OgreClan AI.</> :
		`OgreClan AI isn't open to your account yet.`}</p>;
}

export function ChatPage({ id }: { id?: string }) {
	const me = useMe();
	const start = useMemo(() => new URLSearchParams(location.search), []);
	const [status, setStatus] = useState<Status | null>(null);
	const [statusError, setStatusError] = useState('');
	const [chats, setChats] = useState<ChatSummary[] | null>(null);
	const [retentionDays, setRetentionDays] = useState(180);
	const [chat, setChat] = useState<ChatSummary | null>(null);
	const [messages, setMessages] = useState<Message[]>([]);
	const [loading, setLoading] = useState(false);
	const [missing, setMissing] = useState(false);
	const [league, setLeague] = useState(start.get('league') || '');
	const [format, setFormat] = useState(start.get('format') || '');
	const [page] = useState(fromPage);
	const [text, setText] = useState('');
	const [busy, setBusy] = useState(false);
	const [problem, setProblem] = useState<{ error: string, requestId?: string } | null>(null);
	const [clarify, setClarify] = useState<{ question: string, options: LeagueOption[], content: string } | null>(null);
	const [showList, setShowList] = useState(false);
	const [title, setTitle] = useState<string | null>(null);
	const end = useRef<HTMLDivElement>(null);

	const loadChats = () => api.get<{ chats: ChatSummary[], retentionDays: number }>('/api/ai/chats').then(result => {
		setChats(result.chats);
		setRetentionDays(result.retentionDays);
	}, () => setChats([]));

	useEffect(() => {
		if (!me?.user) return;
		api.get<Status>('/api/ai/status').then(setStatus, (e: Error) => setStatusError(e.message));
		void loadChats();
	}, [me?.user?.id]);

	// the conversation at this address (not again for one just made here)
	useEffect(() => {
		setProblem(null);
		setClarify(null);
		setShowList(false);
		setTitle(null);
		if (!id) {
			// a new conversation: the league and format chosen for the last one don't carry over
			if (chat) {
				setLeague('');
				setFormat('');
			}
			setChat(null);
			setMessages([]);
			setMissing(false);
			return;
		}
		if (chat?.id === id) return;
		if (!me?.user) return;
		setLoading(true);
		api.get<{ chat: ChatSummary, messages: Message[] }>(`/api/ai/chats/${encodeURIComponent(id)}`).then(result => {
			setChat(result.chat);
			setMessages(result.messages);
			setLeague(result.chat.league || '');
			setFormat(result.chat.format || '');
			setMissing(false);
		}, () => {
			setChat(null);
			setMessages([]);
			setMissing(true);
		}).finally(() => setLoading(false));
	}, [id, me?.user?.id]);

	useEffect(() => {
		end.current?.scrollIntoView({ block: 'end' });
	}, [messages, busy, clarify]);

	async function send(content: string, chosenLeague = league) {
		setBusy(true);
		setProblem(null);
		setClarify(null);
		const before = messages;
		setMessages([...before, { id: 'asking', role: 'user', content }]);
		setText('');
		try {
			const body = {
				content, ...(chosenLeague ? { league: chosenLeague } : {}), ...(format.trim() ? { format: format.trim() } : {}),
				...(page && !chosenLeague && !chat ? { page } : {}),
			};
			const reply = chat
				? await api.post<Reply>(`/api/ai/chats/${chat.id}/messages`, body)
				: await api.post<Reply>('/api/ai/chats', body);
			if (reply.kind === 'clarify') {
				setMessages(before);
				setClarify({ question: reply.question || 'Which league is this about?', options: reply.options || [], content });
				return;
			}
			const saved = reply.chat!;
			setMessages([...before, ...reply.messages!]);
			setChat(saved);
			setLeague(saved.league || '');
			setChats(list => [saved, ...(list || []).filter(c => c.id !== saved.id)]);
			if (!chat) navigate(`/chat/${saved.id}`);
			if (status?.today) setStatus({ ...status, today: { ...status.today, questions: status.today.questions + 1 } });
		} catch (e) {
			// the question goes back in the box, to send again
			setMessages(before);
			setText(content);
			const data = e instanceof ApiError ? e.data as { requestId?: string, retryAfterSeconds?: number } : {};
			const wait = data.retryAfterSeconds && data.retryAfterSeconds < 3600 ? ` (in about ${Math.max(1, Math.round(data.retryAfterSeconds / 60))} min)` : '';
			setProblem({ error: `${(e as Error).message}${wait}`, requestId: data.requestId });
		} finally {
			setBusy(false);
		}
	}

	function ask() {
		const question = text.trim();
		if (question && !busy) void send(question);
	}

	/** A change to this conversation (its title, league or format), saved at once */
	async function change(update: Partial<Record<'title' | 'league' | 'format', string | null>>) {
		if (!chat) return;
		try {
			const result = await api.patch<{ chat: ChatSummary }>(`/api/ai/chats/${chat.id}`, update);
			setChat(result.chat);
			setChats(list => (list || []).map(c => c.id === result.chat.id ? result.chat : c));
		} catch (e) {
			setProblem({ error: (e as Error).message });
			if (update.league !== undefined) setLeague(chat.league || '');
		}
	}

	async function remove() {
		if (!chat || !confirm(`Delete "${chat.title}"? It's gone for good.`)) return;
		await api.delete(`/api/ai/chats/${chat.id}`).catch((e: Error) => setProblem({ error: e.message }));
		setChats(list => (list || []).filter(c => c.id !== chat.id));
		navigate('/chat');
	}

	async function clearAll() {
		if (!confirm('Delete every conversation of yours, here and in Discord? This can\'t be undone.')) return;
		await api.delete('/api/ai/chats').catch((e: Error) => setProblem({ error: e.message }));
		setChats([]);
		navigate('/chat');
	}

	if (!me) return <Loading />;
	if (!me.user) {
		return <div class="card">
			<h1>OgreClan AI</h1>
			<p>Ask about Pokémon, matchups, Speed, your team and your draft leagues. Sign in to use it.</p>
			<a class="button primary" href={signInUrl()}>Sign in with Discord</a>
		</div>;
	}
	if (statusError) return <p class="notice bad">{statusError}</p>;
	if (!status) return <Loading />;
	if (!status.enabled || !status.allowed) return <div class="card"><h1>OgreClan AI</h1><Notice status={status} /></div>;

	const leagueName = (ref: string | null) => status.leagues.find(l => l.ref === ref)?.name ?? ref;
	return <div class={`chat-app${showList ? ' show-list' : ''}`}>
		<aside class="chat-list" aria-label="Your conversations">
			<button type="button" class="primary chat-new" onClick={() => navigate('/chat')} disabled={!id && !messages.length}>New conversation</button>
			{chats === null ? <Loading /> : !chats.length ? <p class="muted small">Your conversations show here.</p> : <nav>
				{chats.map(item => <Link key={item.id} href={`/chat/${item.id}`} class={item.id === chat?.id ? 'active' : ''}>
					<span class="chat-list-title">{item.title}</span>
					<span class="small muted">{SOURCES[item.source] ? <>{SOURCES[item.source]} · </> : ''}{ago(item.updatedAt)}</span>
				</Link>)}
			</nav>}
			<p class="small muted chat-privacy">Saved to your account, so only you see them: delete one, or{' '}
				<button type="button" class="link small" onClick={() => void clearAll()} disabled={!chats?.length}>clear your history</button>, at any time.
				Conversations you don't add to for {retentionDays} days are deleted. Discord DM conversations show here too; Discord channels
				are public, so they don't.</p>
		</aside>

		<section class="chat">
			<div class="row chat-head">
				<button type="button" class="small chat-list-toggle" onClick={() => setShowList(!showList)} aria-expanded={showList}>Conversations</button>
				{chat && title !== null
					? <form class="row" style="flex:1" onSubmit={e => {
						e.preventDefault();
						if (title.trim()) void change({ title: title.trim() });
						setTitle(null);
					}}>
						<input value={title} maxLength={120} aria-label="Conversation title" style="flex:1"
							onInput={e => setTitle((e.target as HTMLInputElement).value)} />
						<button type="submit" class="small">Save</button>
					</form>
					: <h1 class="chat-title">{chat?.title || 'OgreClan AI'}</h1>}
				{chat && title === null && <span class="row">
					<button type="button" class="small" onClick={() => setTitle(chat.title)}>Rename</button>
					<button type="button" class="small danger" onClick={() => void remove()}>Delete</button>
				</span>}
			</div>
			<p class="muted small">Answers come from OgreClan's own data: Showdown's Pokémon and formats, and your leagues. It can be wrong,
				and it doesn't calculate damage. {status.today && <>{status.today.questions} of {status.today.limit} questions used today.</>}</p>
			{status.freeTier && <p class="notice small">{status.vertex ? `This runs on a free trial of Google's model while it's tested.` :
				`This runs on a free model while it's tested, and its provider may read what you send to improve its products.`} Don't share
				anything private. Private leagues are left out.</p>}

			<div class="row small chat-context">
				<label class="row">League
					<select value={league} disabled={busy} onChange={e => {
						const value = (e.target as HTMLSelectElement).value;
						setLeague(value);
						if (chat) void change({ league: value || null });
					}}>
						<option value="">{page && !chat ? 'The one you came from' : status.leagues.length === 1 ? `${status.leagues[0]!.name} (yours)` : 'Worked out for you'}</option>
						{status.leagues.map(option => <option key={option.ref} value={option.ref}>{option.name}</option>)}
						{league && !status.leagues.some(o => o.ref === league) && <option value={league}>{leagueName(league)}</option>}
					</select>
				</label>
				<label class="row">Format
					<input class="small" value={format} maxLength={300} placeholder="optional, e.g. gen9vgc2026regmc" disabled={busy}
						onInput={e => setFormat((e.target as HTMLInputElement).value)}
						onBlur={() => {
							if (chat && (format.trim() || null) !== chat.format) void change({ format: format.trim() || null });
						}} />
				</label>
			</div>

			<div class="chat-log" aria-live="polite">
				{loading && <Loading />}
				{missing && <p class="notice">There's no conversation of yours here. <Link href="/chat">Start a new one</Link>.</p>}
				{!loading && !missing && !messages.length && <p class="muted">Try “What beats Garchomp?”, “Is my team slow?” or “Who's still available for 10 points or less?”</p>}
				{messages.map(message => <div key={message.id} class={`chat-message ${message.role}`}>
					{/* plain text only: the answer is never HTML */}
					<div class="chat-text">{message.content}</div>
					{message.meta && (message.meta.league || message.meta.format || message.meta.truncated) && <div class="small muted">
						{[message.meta.league || message.meta.format ? `About ${[message.meta.league, message.meta.format].filter(Boolean).join(', ')}` : '',
							message.meta.truncated ? 'cut short' : ''].filter(Boolean).join(' · ')}</div>}
				</div>)}
				{busy && <div class="chat-message assistant"><span class="muted">Thinking…</span></div>}
				{clarify && <div class="chat-message assistant">
					<div class="chat-text">{clarify.question}</div>
					<div class="row" style="margin-top:6px">{clarify.options.map(option => <button key={option.ref} type="button" class="small" onClick={() => {
						setLeague(option.ref);
						void send(clarify.content, option.ref);
					}}>{option.name}</button>)}</div>
				</div>}
				{problem && <div class="notice bad small">{problem.error}{problem.requestId && <> (request {problem.requestId.slice(0, 8)})</>}</div>}
				<div ref={end} />
			</div>
			<form class="chat-form" onSubmit={e => {
				e.preventDefault();
				ask();
			}}>
				<textarea rows={3} maxLength={4000} placeholder="Ask about Pokémon, your team or your league…" value={text} disabled={busy || missing}
					aria-label="Your question"
					onInput={e => setText((e.target as HTMLTextAreaElement).value)}
					onKeyDown={e => {
						if (e.key === 'Enter' && !e.shiftKey) {
							e.preventDefault();
							ask();
						}
					}} />
				<button type="submit" class="primary" disabled={busy || missing || !text.trim()}>Ask</button>
			</form>
		</section>
	</div>;
}
